// ===================
// P(HIJACK) CALCULATION
// ===================
// Probability that an opponent will hijack a double auction by offering a matching card
//
// Formula: P(at least one unaccounted card is in opponent hands)
//   = 1 - P(all unaccounted cards are in deck)
//   = 1 - C(deck, unaccounted) / C(deck + opp, unaccounted)

import type { Artist, GameState } from '../../../types/game'
import { CARD_DISTRIBUTION } from '../../../engine/constants'

/** 30% threshold - safe to play double alone if P(Hijack) < 30% */
export const HIJACK_THRESHOLD = 0.30

/**
 * Calculate P(Hijack) - probability opponent has a matching card
 *
 * @param unaccounted - Cards of this artist not in AI's hand or played (total - played - AI)
 * @param deckSize - Cards remaining in deck (cannot hijack yet)
 * @param opponentHandSize - Total cards in all opponents' hands (can hijack)
 * @returns Probability between 0 and 1
 *
 * @throws Error if calculated values are invalid (negative, etc.)
 */
export function calculateHijackProbability(
  unaccounted: number,
  deckSize: number,
  opponentHandSize: number
): number {
  // Edge case: No unaccounted cards = no hijack possible
  if (unaccounted === 0) return 0

  // Edge case: Deck empty = all unaccounted cards must be in opponent hands
  if (deckSize === 0) return 1

  // Edge case: No opponent cards = can't hijack
  if (opponentHandSize === 0) return 0

  // Edge case: More unaccounted than deck slots = guaranteed hijack
  if (unaccounted > deckSize) return 1

  // Validate inputs
  if (unaccounted < 0) {
    throw new Error(`Negative unaccounted cards: ${unaccounted}`)
  }
  if (deckSize < 0) {
    throw new Error(`Negative deck size: ${deckSize}`)
  }
  if (opponentHandSize < 0) {
    throw new Error(`Negative opponent hand size: ${opponentHandSize}`)
  }

  const totalUnknown = deckSize + opponentHandSize

  // Calculate probability ALL unaccounted cards are in deck
  let allInDeckProb = 1.0
  for (let i = 0; i < unaccounted; i++) {
    allInDeckProb *= (deckSize - i) / (totalUnknown - i)
  }

  // Flip: probability at least one is in opponent hands
  return 1 - allInDeckProb
}

/**
 * Determine if AI can safely play a double card without offering a second card
 *
 * @param artist - The artist of the double card
 * @param gameState - Current game state
 * @param playerIndex - AI player index
 * @returns true if P(Hijack) < 30% (safe to play alone)
 *
 * @throws Error if artist invalid, playerIndex out of bounds, or calculation fails
 */
export function canPlayDoubleSafely(
  artist: Artist,
  gameState: GameState,
  playerIndex: number
): boolean {
  // Validate inputs
  if (!artist || typeof artist !== 'string') {
    throw new Error('Invalid artist')
  }

  if (playerIndex < 0 || playerIndex >= gameState.players.length) {
    throw new Error(`Player index ${playerIndex} out of bounds`)
  }

  if (!CARD_DISTRIBUTION[artist]) {
    throw new Error(`Unknown artist: ${artist}`)
  }

  // Get cards played this round
  const cardsPlayed = gameState.round.cardsPlayedPerArtist[artist] || 0

  // Count cards in AI hand
  const player = gameState.players[playerIndex]
  const cardsInHand = player.hand.filter(c => c.artist === artist).length

  // Calculate unaccounted cards
  const totalCards = CARD_DISTRIBUTION[artist]
  const accountedFor = cardsPlayed + cardsInHand
  const unaccounted = totalCards - accountedFor

  if (unaccounted < 0) {
    throw new Error(`Negative unaccounted cards for ${artist}: ${unaccounted}`)
  }

  // Get deck size
  const deckSize = gameState.deck.length
  if (deckSize < 0) {
    throw new Error(`Negative deck size: ${deckSize}`)
  }

  // Calculate opponent hand size
  let opponentHandSize = 0
  for (let i = 0; i < gameState.players.length; i++) {
    if (i !== playerIndex) {
      opponentHandSize += gameState.players[i].hand.length
    }
  }

  // Calculate P(Hijack)
  const pHijack = calculateHijackProbability(unaccounted, deckSize, opponentHandSize)

  // Safe if below 30% threshold
  return pHijack < HIJACK_THRESHOLD
}
